import { Box } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import {
  SynchronizedLinesRowType,
  WorkstationDownTimeRowType,
} from '../Scheduler.types'
import { SchedulerSynchronizedLinesTable } from '../SchedulerSynchronizedLines.muiTable'
import {
  useScheduleSOR,
  useScheduleSORFull,
  useUpdateSchedulerSOR,
  useUploadScheduleDataFile,
} from './SOR.data'

// Master (tenant-wide) synchronized lines tab on the SOR page.
// Reads/writes the master Delta at SchedulerApp/Inputs/SynchronizedLinesDelta/.
// New scheduler runs are seeded from this master at creation time
// (see API/.../sheduler_model.py create_schedule). Per-scheduler edits remain
// scoped to each iteration via the existing Raw Data tab.
export const SORSynchronizedLines = () => {
  const API = 'synchronized-lines'
  const { data: responseData, isLoading } =
    useScheduleSORFull<SynchronizedLinesRowType>(API)
  const serverData = responseData?.data ?? []
  const predicate = responseData?.predicate
  const availableLines = responseData?.availableLines ?? []

  const { data: downtimeResponse } = useScheduleSOR('workstation-downtime')
  const downtimeRows: WorkstationDownTimeRowType[] = (downtimeResponse?.data ??
    []) as WorkstationDownTimeRowType[]
  const availableLocations = useMemo(
    () =>
      [
        ...new Set(downtimeRows.map((r) => r.site_location).filter(Boolean)),
      ].sort((a, b) => a.localeCompare(b)),
    [downtimeRows]
  )

  const [localNewRows, setLocalNewRows] = useState<SynchronizedLinesRowType[]>(
    []
  )
  const [nextNewRowId, setNextNewRowId] = useState(-1)

  const data = useMemo(
    () => [...serverData, ...localNewRows],
    [serverData, localNewRows]
  )

  const onUpdateSuccess = () => {
    setLocalNewRows([])
    toast.success('Updated successfully.')
  }
  const onUpdateFailure = () => {
    toast.error('Failed to update.')
  }
  const { mutate: saveData, isLoading: isUpdating } =
    useUpdateSchedulerSOR<SynchronizedLinesRowType>(
      onUpdateSuccess,
      onUpdateFailure
    )

  const [allChangedRows, setAllChangedRows] = useState<
    Record<number, SynchronizedLinesRowType>
  >({})

  const updateTableData = (
    changedRows: Record<number, SynchronizedLinesRowType>
  ) => {
    setAllChangedRows({ ...allChangedRows, ...changedRows })
  }

  const handleAddGroup = () => {
    const newRow: SynchronizedLinesRowType = {
      id: nextNewRowId,
      groupName: `Group ${Math.abs(nextNewRowId)}`,
      lines: '',
      changeoverTime: 0,
      productMasterType: '',
      active: 1,
      periods: '',
    }
    setLocalNewRows([...localNewRows, newRow])
    setNextNewRowId(nextNewRowId - 1)
  }

  useEffect(() => {
    if (Object.keys(allChangedRows).length > 0) {
      saveData({ data: allChangedRows, API })
      setAllChangedRows({})
    }
  }, [allChangedRows])

  const [updateRowsStatus, setUpdateRowsStatus] = useState({})

  const onUploadSuccess = (mergeInfo?: Record<string, unknown>) => {
    setUpdateRowsStatus(mergeInfo ?? {})
    toast.success('Data uploaded successfully')
  }
  const onUploadFailure = () => {
    toast.error('Failed to upload data')
  }
  const { mutate: uploadScheduleData, isLoading: isUploading } =
    useUploadScheduleDataFile(onUploadSuccess, onUploadFailure)

  const uploadFile = (file: File) => {
    uploadScheduleData({ file, API })
  }

  return (
    <Box>
      <SchedulerSynchronizedLinesTable
        data={data}
        isLoading={isLoading}
        updateTableData={updateTableData}
        uploadScheduleData={uploadFile}
        isUploading={isUploading}
        isSaving={isUpdating}
        updateRowsStatus={updateRowsStatus}
        predicate={predicate}
        onAddGroup={handleAddGroup}
        availableLines={availableLines}
        availableLocations={availableLocations}
      />
    </Box>
  )
}
